/* ─── Trigger (pass-through, minimal styling) ─── */

.trigger {
  cursor: pointer;
}

/* ─── Backdrop ─── */

.backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  background: rgb(var(--md-scrim) / var(--scrim-opacity));
  backdrop-filter: blur(4px);
  transition: opacity var(--motion-duration-medium1)
    var(--motion-easing-standard);
}

.backdrop[data-starting-style],
.backdrop[data-ending-style] {
  opacity: 0;
}

/* ─── Popup ─── */

.popup {
  position: fixed;
  z-index: var(--z-modal);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: var(--dialog-max-width);
  max-height: calc(100dvh - 4rem);
  overflow-y: auto;
  padding: var(--space-6);
  background: var(--md-surface-container-high);
  border-radius: var(--shape-xl);
  box-shadow: var(--elevation-3);
  display: flex;
  flex-direction: column;
  transition: opacity var(--motion-duration-medium1)
      var(--motion-easing-standard),
    transform var(--motion-duration-medium1) var(--motion-easing-standard);
}

/* When .body is present, disable popup-level scroll and let body scroll */
.popup:has(.body) {
  overflow-y: hidden;
}

.body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Compensate for overflow-y: auto forcing overflow-x: auto (CSS spec),
     which clips focus rings and box-shadows on flush-edge children.
     Negative margin keeps the body the same visual width. */
  padding-inline: var(--space-1);
  margin-inline: calc(-1 * var(--space-1));
}

.actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  padding-block-start: var(--space-4);
  border-block-start: 1px solid var(--md-outline-variant);
}

.actionsStart {
  flex: 1 1 auto;
  min-inline-size: 0;
}

/* ─── Tabbed dialog layout ─── */

.dialogTabs {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  margin: 0 calc(-1 * var(--space-6)) calc(-1 * var(--space-6));
}

.dialogTabsList {
  flex-shrink: 0;
  padding-inline: var(--space-6);
}

.dialogTabs > .body {
  padding-inline: var(--space-6);
  margin-inline: 0;
  overflow-x: hidden;
}

.popup[data-starting-style],
.popup[data-ending-style] {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.95);
}

/* ─── Alignment variants ─── */

.align-top {
  top: 2rem;
  transform: translate(-50%, 0);
}

.align-top[data-starting-style],
.align-top[data-ending-style] {
  opacity: 0;
  transform: translate(-50%, 0) scale(0.95);
}

/* ─── Size variants ─── */

.sm {
  --dialog-max-width: 400px;
}

.md {
  --dialog-max-width: 560px;
}

.lg {
  --dialog-max-width: 720px;
}

.xl {
  --dialog-max-width: 900px;
}

.full {
  --dialog-content-max-width: 56rem;

  max-width: 100vw;
  max-height: 100dvh;
  padding: 0;
  border-radius: 0;
  inset: 0;
  top: 0;
  left: 0;
  transform: none;
}

.full .body {
  margin-inline: 0;
  padding-block: var(--space-6);
  padding-inline: max(
    var(--space-4),
    calc((100% - var(--dialog-content-max-width)) / 2)
  );
}

.full .actions {
  flex-wrap: wrap;
  padding-block: var(--space-3);
  padding-inline: var(--space-4);
  border-block-start: none;
}

.full[data-starting-style],
.full[data-ending-style] {
  opacity: 0;
  transform: scale(0.95);
}

/* ─── Full screen at the compact window size ─── */

.fullScreenOnCompact {
  inset: 0;
  max-width: none;
  max-height: none;
  border-radius: 0;
  transform: none;
}

.fullScreenOnCompact[data-starting-style],
.fullScreenOnCompact[data-ending-style] {
  transform: none;
}

@media (--window-medium) {
  .fullScreenOnCompact {
    inset: 50% auto auto 50%;
    max-width: var(--dialog-max-width);
    max-height: calc(100dvh - 4rem);
    border-radius: var(--shape-xl);
    transform: translate(-50%, -50%);
  }

  .fullScreenOnCompact[data-starting-style],
  .fullScreenOnCompact[data-ending-style] {
    transform: translate(-50%, -50%) scale(0.95);
  }
}

/* ─── Close button ─── */

.close {
  position: absolute;
  top: var(--space-3);
  inset-inline-end: var(--space-3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: none;
  border-radius: var(--shape-full);
  background: transparent;
  color: var(--md-on-surface-variant);
  cursor: pointer;
  z-index: 1;
}

.close::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0;
  transition: opacity var(--motion-duration-short3);
  pointer-events: none;
}

@media (hover: hover) {
  .close:hover::before {
    opacity: var(--state-hover);
  }
}

.close:focus-visible::before {
  opacity: var(--state-focus);
}

.close:active::before {
  opacity: var(--state-pressed);
}

.close svg {
  width: 1rem;
  height: 1rem;
}

/* ─── Title ─── */

.title {
  font: var(--type-headline-sm);
  letter-spacing: var(--tracking-headline-sm);
  font-family: var(--font-sans);
  color: var(--md-on-surface);
  margin: 0;
  padding-block-end: var(--space-4);
}

/* ─── Description ─── */

.description {
  font: var(--type-body-lg);
  letter-spacing: var(--tracking-body-lg);
  font-family: var(--font-sans);
  color: var(--md-on-surface-variant);
  margin: 0;
}

.description + .body {
  margin-block-start: var(--space-4);
}

/* ─── App bar (full-screen dialogs) ─── */

.appBar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding-block: var(--space-3);
  padding-inline: var(--space-4);
}

.appBarClose {
  position: relative;
  inset-block-start: auto;
  inset-inline-end: auto;
  flex-shrink: 0;
}

.appBarClose svg {
  width: var(--icon-size-lg);
  height: var(--icon-size-lg);
}

.appBarTitles {
  flex: 1;
  min-inline-size: 0;
}

/* MD3's full-screen dialog titles its app bar in title-large. */
.appBar .title {
  font: var(--type-title-lg);
  letter-spacing: var(--tracking-title-lg);
  padding-block-end: 0;
}

.appBar .description {
  font: var(--type-body-md);
  letter-spacing: var(--tracking-body-md);
}

.appBarEnd {
  flex-shrink: 0;
  display: flex;
  gap: var(--space-2);
}
